import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import {
  estaAutenticado,
  precisaEscolherEmpresa,
  semVinculo,
  usarSessao,
  vinculoCorrente,
  type EstadoSessao,
  type TokensSessao,
} from '../estado/sessao';
import { entrar, sair, type CredenciaisLogin } from '../servicos/auth';
import type { IdentidadeDoToken, VinculoDoToken } from '../servicos/jwt';

/**
 * Acesso à sessão pelas telas.
 *
 * Os seletores são individuais de propósito: um seletor que devolve objeto novo
 * a cada render faz o zustand disparar re-render em loop.
 */

export function useIdentidade(): IdentidadeDoToken | null {
  return usarSessao((estado) => estado.identidade);
}

export function useAutenticado(): boolean {
  return usarSessao(estaAutenticado);
}

export function useEmpresaCorrente(): string | null {
  return usarSessao((estado) => estado.empresaCorrente);
}

export function useVinculoCorrente(): VinculoDoToken | null {
  return usarSessao(vinculoCorrente);
}

/** RN-603 — há mais de um vínculo e o usuário ainda não escolheu. */
export function usePrecisaEscolherEmpresa(): boolean {
  return usarSessao(precisaEscolherEmpresa);
}

/** Conta autenticada sem nenhum vínculo ativo — estado legítimo, não erro. */
export function useSemVinculo(): boolean {
  return usarSessao(semVinculo);
}

export function useMotivoEncerramento(): string | null {
  return usarSessao((estado) => estado.motivoEncerramento);
}

export function useEscolherEmpresa(): EstadoSessao['escolherEmpresa'] {
  return usarSessao((estado) => estado.escolherEmpresa);
}

/** Volta ao seletor de empresa (RN-603). */
export function useLimparEmpresa(): EstadoSessao['limparEmpresa'] {
  return usarSessao((estado) => estado.limparEmpresa);
}

/**
 * Login.
 *
 * Não decide navegação: quem chama decide para onde ir, porque o destino
 * depende de haver ou não escolha de empresa pendente (RN-603).
 */
export function useEntrar(): UseMutationResult<TokensSessao, Error, CredenciaisLogin> {
  const clienteDeConsultas = useQueryClient();

  return useMutation({
    mutationFn: (credenciais: CredenciaisLogin) => entrar(credenciais),
    onSuccess: () => {
      // Cache da sessão anterior não pode vazar para a nova: são escopos de
      // dados diferentes, e um deles pode ser de outra empresa.
      clienteDeConsultas.clear();
    },
  });
}

/**
 * Logout.
 *
 * A sessão local é encerrada mesmo se a revogação no servidor falhar
 * (`servicos/auth.ts`), então o cache é limpo nos dois desfechos. O erro, se
 * houver, é devolvido para a tela dizer o que aconteceu de verdade — em vez de
 * sumir em silêncio, como no legado.
 */
export function useSair(): UseMutationResult<void, Error, void> {
  const clienteDeConsultas = useQueryClient();

  return useMutation({
    mutationFn: () => sair(),
    onSettled: () => {
      clienteDeConsultas.clear();
    },
  });
}
